import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users } from 'lucide-react';
import { ImageBox } from '../ui/ImageBox';
import type { Event } from '../../types/event';

/**
 * One event, linking to its own page.
 *
 * Stretched link: the whole card is a single control whose accessible name
 * is the event title, with no nested interactive elements. The "View
 * details" row is `aria-hidden` because it is an affordance for the link
 * the heading already provides, not a second destination.
 */
export function EventCard({
  event
}: {
  event: Event;
}) {
  const isWalkIn = event.booking === 'walk-in';
  const isSoldOut = !isWalkIn && event.spotsLeft === 0;
  // Only meaningful for a bookable event; walk-ins publish no capacity.
  const isFullyOpen = event.spotsLeft !== undefined && event.spotsLeft === event.capacity;
  return <article data-component-id="C18" className="group relative flex h-full flex-col overflow-hidden rounded-container border border-hairline bg-white transition-[border-color,box-shadow] duration-200 ease-out-expo focus-within:border-flame focus-within:shadow-lg hover:border-flame hover:shadow-lg" data-id="element-122" data-mp="38k3q6hk9ea22">
      <ImageBox src={event.image} alt={event.imageAlt} ratio="16/9" className="border-b border-hairline" imageClassName="transition-transform duration-300 ease-out-expo group-hover:scale-[1.03] motion-reduce:transform-none" data-id="element-123" data-mp="2pbr6k5xz03h1" />

      <div className="flex flex-1 flex-col p-5" data-id="element-124" data-mp="21hpuq7zqswkx">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-[0.16em] text-ink-muted" data-id="element-125" data-mp="26epxoa1apj7t">
          {event.when}
        </p>

        <h3 className="font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-126" data-mp="2bdb2kmrhsr2l">
          <Link to={`/whats-on/${event.id}`} className="text-left after:absolute after:inset-0 after:rounded-container after:content-[''] focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-flame focus-visible:after:ring-offset-2" data-id="element-127" data-mp="2gdp89y5mfoxg">
            {event.title}
          </Link>
        </h3>

        <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/80" data-id="element-128" data-mp="1roi4cbn5stkf">
          {event.blurb}
        </p>

        <div className="mt-5 flex items-center gap-2 border-t border-hairline pt-4 text-sm" data-id="element-129" data-mp="1mtch99i0t8ur">
          {/* Status carries a shape, a colour and a text label. */}
          <span className={`flex items-center gap-1.5 font-bold ${isSoldOut ? 'text-rust' : isWalkIn || isFullyOpen ? 'text-moss' : 'text-ink-muted'}`} data-id="element-130" data-mp="1wqh4lcnlrad5">
            <span aria-hidden="true" data-id="element-131" data-mp="1hsotqxt03a5a">
              {isSoldOut ? '■' : isWalkIn || isFullyOpen ? '●' : '▲'}
            </span>
            {/* The `spotsLeft !== undefined` arm matters: the field is
                optional (D46), so a bookable event without one must fall
                back to a true statement rather than "undefined spaces
                left". */}
            {isSoldOut ? 'Fully booked' : isWalkIn ? 'Walk in, no booking' : isFullyOpen || event.spotsLeft === undefined ? 'Spaces available' : `${event.spotsLeft} spaces left`}
          </span>

          {event.price !== undefined ? <span className="ml-auto font-bebas text-2xl leading-none tracking-wide text-flame" data-id="element-132" data-mp="66g0i0fk3gwo">
              £{event.price}
            </span> : <span className="ml-auto flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-moss" data-id="element-133" data-mp="tuz9ki6fjxpt">
              <Users className="h-3.5 w-3.5" aria-hidden="true" data-id="element-134" data-mp="jyw7tdfhq13e" />
              Free
            </span>}
        </div>

        <span aria-hidden="true" className="mt-4 flex items-center gap-1.5 text-sm font-bold text-ink transition-colors duration-200 ease-out-expo group-hover:text-flame" data-id="element-135" data-mp="owpcu3524p23">
          View details
          <ArrowRight className="h-4 w-4 transition-transform duration-200 ease-out-expo group-hover:translate-x-1 motion-reduce:transform-none" data-id="element-136" data-mp="39pj1pu5wq0td" />
        </span>
      </div>
    </article>;
}